import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@mui/material';
import { useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import PostService from '../../services/PostService';
import { taskErrorMessage } from '../../services/TaskTrackingService';
import { weekdayDay } from './calendarUi';

const languageWord = (n) => `${n} language${n === 1 ? '' : 's'}`;

/** Languages not on the picked date, as the question the dialog asks. */
const moveQuestions = (languages, date) =>
    languages
        .filter((l) => l.date && l.date !== date)
        .map((l) => `${l.languageLabel} is on ${weekdayDay(l.date)}. Move it to ${weekdayDay(date)} too?`);

/**
 * Set posting date (rev6 Part 5, D66): one date for every language of a post. Languages that have
 * gone out keep theirs. Languages on different dates ask first, and the
 * server sends the dates back if they've changed since this opened.
 */
const SetPostingDateDialog = ({ post, onClose, onSaved }) => {
    useTrackUsage('SetPostingDateDialog', 'dialog', 'posts');
    const live = (post.strip || []).filter((e) => e.state !== 'CANCELLED');
    const [languages, setLanguages] = useState(() =>
        live
            .filter((e) => !e.hasLink)
            .map((e) => ({ languageKey: e.languageKey, languageLabel: e.languageLabel, date: e.targetDate || null })),
    );
    const [kept, setKept] = useState(live.filter((e) => e.hasLink).length);
    const dates = [...new Set(languages.map((l) => l.date))];
    const differ = dates.length > 1;
    const [date, setDate] = useState(dates.length === 1 && dates[0] ? dates[0] : '');
    const [changed, setChanged] = useState(false);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [clearing, setClearing] = useState(false);

    const hasDate = dates.some(Boolean);
    const questions = differ && date ? moveQuestions(languages, date) : [];

    const save = async (value) => {
        setBusy(true);
        setError('');
        try {
            const result = await PostService.setPostingDate(post.postId, {
                date: value || null,
                shown: Object.fromEntries(languages.map((l) => [l.languageKey, l.date])),
                // The question is on screen, so saving is the answer
                allLanguages: differ,
            });
            if (result.applied) {
                onSaved();
                return;
            }
            setLanguages(result.languages);
            setKept(result.keptWithLink);
            setChanged(true);
        } catch (e) {
            setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{hasDate ? 'Change posting date' : 'Set posting date'}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 1.5 }}>
                    {languages.length === 0
                        ? `All ${languageWord(kept)} have gone out, so they keep their date.`
                        : `For ${languages.length === 1 ? languages[0].languageLabel : `${languages.length} languages`}. Stages not done yet are planned again from this date.`}
                </DialogContentText>
                {languages.length > 0 && (
                    <TextField
                        type="date"
                        fullWidth
                        label="Posting date"
                        InputLabelProps={{ shrink: true }}
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                    />
                )}
                {languages.length > 0 && kept > 0 && (
                    <DialogContentText variant="body2" sx={{ mt: 1.5 }}>
                        {languageWord(kept)} already gone out {kept === 1 ? 'keeps its' : 'keep their'} date.
                    </DialogContentText>
                )}
                {clearing && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                        Clear the date for {languages.length === 1 ? languages[0].languageLabel : `all ${languages.length} languages`}?
                    </Alert>
                )}
                {changed && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                        The dates changed while this was open. Check them and save again.
                    </Alert>
                )}
                {questions.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                        {questions.map((q) => (
                            <DialogContentText key={q} variant="body2">
                                {q}
                            </DialogContentText>
                        ))}
                    </Box>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            {clearing ? (
                <DialogActions>
                    <Button onClick={() => setClearing(false)}>Keep the date</Button>
                    <Button variant="contained" color="warning" disabled={busy} onClick={() => save(null)}>
                        Clear date
                    </Button>
                </DialogActions>
            ) : (
                <DialogActions>
                    {hasDate && languages.length > 0 && (
                        <Button color="inherit" disabled={busy} onClick={() => setClearing(true)} sx={{ mr: 'auto' }}>
                            Clear date
                        </Button>
                    )}
                    <Button onClick={onClose}>Cancel</Button>
                    {languages.length > 0 && (
                        <Button variant="contained" disabled={busy || !date} onClick={() => save(date)}>
                            {differ ? 'Move them' : 'Save'}
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default SetPostingDateDialog;
